import {
  type DivIcon,
  type MarkerCluster,
  type MarkerClusterGroup as LeafletMarkerClusterGroup,
  divIcon,
  point,
} from 'leaflet';
import { useCallback, useLayoutEffect, useRef } from 'react';
import MarkerClusterGroup from 'react-leaflet-cluster';

const createCustomClusters = (cluster: MarkerCluster): DivIcon => {
  const childCount = cluster.getChildCount();
  const size = childCount < 10 ? 'small' : childCount < 100 ? 'medium' : 'large';

  return divIcon({
    // div + span are required for proper styling 🙄
    html: `<div><span>${childCount}</span></div>`,
    className: `marker-cluster marker-cluster-${size}`,
    iconSize: point(40, 40),
  });
};

type ClusteredMarkersProps = {
  layer: React.ReactNode;
  spiderfy: boolean;
};

/**
 * Owns the Leaflet cluster group so bulk clearLayers runs whenever this branch
 * unmounts — including when enableClustering flips off while the parent stays mounted.
 */
const ClusteredMarkers = ({ layer, spiderfy }: ClusteredMarkersProps) => {
  const clusterGroupRef = useRef<LeafletMarkerClusterGroup | null>(null);

  // React detaches refs before cleanups run, so hold on to the last instance.
  const handleClusterGroupRef = useCallback((clusterGroup: LeafletMarkerClusterGroup | null) => {
    if (clusterGroup) {
      clusterGroupRef.current = clusterGroup;
    }
  }, []);

  /**
   * Markers unmount individually, and every single removal recalculates the cluster bounds
   * and refreshes all cluster icons, which blocks the main thread on Sites with many
   * Turbines. Clearing the group here first makes each of those removals return early.
   */
  useLayoutEffect(
    () => () => {
      clusterGroupRef.current?.clearLayers();
    },
    []
  );

  return (
    <MarkerClusterGroup
      ref={handleClusterGroupRef}
      disableClusteringAtZoom={13}
      spiderfyOnMaxZoom={spiderfy}
      iconCreateFunction={createCustomClusters}
    >
      {layer}
    </MarkerClusterGroup>
  );
};

interface IMarkerClusterGroupLayerProps {
  layer: React.ReactNode;
  spiderfy?: boolean;
  enableClustering?: boolean;
}

export const MarkerClusterGroupLayer: React.FunctionComponent<IMarkerClusterGroupLayerProps> = ({
  layer,
  spiderfy = false,
  enableClustering,
}) => <>{enableClustering ? <ClusteredMarkers layer={layer} spiderfy={spiderfy} /> : layer}</>;
